// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@loa/ui-components';
import { useNudgeData, useSendNudge } from '../hooks/useNudge';
import { useCoaNudgeData, useSendCoaNudge } from '../hooks/useCoaNudge';
import { profileRequestApi } from '../api/profileRequestApi';
import { policyApi } from '../api/policyApi';
import { EmailComposerPanel } from '../components/email/EmailComposerPanel';
import { RecipientInfoCard } from '../components/email/RecipientInfoCard';
import { PoliciesCard } from '../components/email/PoliciesCard';
import { OutstandingRequestsTable } from '../components/requests/OutstandingRequestsTable';
import { deriveDisplayStatus } from '../components/requests/RequestDisplayStatusBadge';
import { NudgeRateLimitWarning } from '../components/nudge/NudgeRateLimitWarning';
import { NudgeHistoryDisplay } from '../components/nudge/NudgeHistoryDisplay';
import { canNudge } from '../types/nudge';
import type { ProfileRequestDTO } from '../types/profileRequest';
import type { Policy } from '../types/policy';

/**
 * Full-page composer for advisor-triggered provider nudges, unified across
 * LOA and CoA document types.
 *
 * <p>Routes:
 * <ul>
 *   <li>{@code /relationships/:relationshipId/policies/:policyId/nudge}
 *       — LOA nudge (legacy modal replacement)</li>
 *   <li>{@code /relationships/:relationshipId/coa/:coaId/policies/:policyId/nudge}
 *       — CoA nudge (new advisor-triggered surface)</li>
 * </ul>
 *
 * <p>Same EmailComposerPanel layout for both; the CoA route additionally
 * shows the resolved agency code on the policy card.
 */
export function NudgeProviderPage() {
  const { relationshipId, policyId, coaId } = useParams<{
    relationshipId: string;
    policyId: string;
    coaId?: string;
  }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const isCoaFlow = Boolean(coaId);

  // Only ONE of the hooks does real work per render — we disable the other
  // by passing an empty id + enabled=false.
  const loa = useNudgeData(isCoaFlow ? '' : (policyId ?? ''), !isCoaFlow);
  const coa = useCoaNudgeData(coaId ?? '', policyId ?? '', isCoaFlow);
  const nudgeData = isCoaFlow ? coa.data : loa.data;
  const isLoading = isCoaFlow ? coa.isLoading : loa.isLoading;
  const fetchError = isCoaFlow ? coa.error : loa.error;

  const sendLoa = useSendNudge(policyId ?? '');
  const sendCoa = useSendCoaNudge(coaId ?? '', policyId ?? '');

  // Outstanding requests + policies for this relationship, fetched so the page
  // can show the outstanding-requests table (replaces the legacy policies
  // table). Best-effort — a failure leaves the table empty, never blocks the
  // composer.
  const [profileRequests, setProfileRequests] = useState<ProfileRequestDTO[]>([]);
  const [profilePolicies, setProfilePolicies] = useState<Policy[]>([]);
  useEffect(() => {
    if (!relationshipId) return;
    let cancelled = false;
    (async () => {
      try {
        const [requestPage, pols] = await Promise.all([
          profileRequestApi.list(relationshipId),
          policyApi.getPoliciesByProfile(relationshipId),
        ]);
        if (cancelled) return;
        setProfileRequests(requestPage.content);
        setProfilePolicies(pols);
      } catch {
        if (cancelled) return;
        setProfileRequests([]);
        setProfilePolicies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [relationshipId]);

  const policyById = useMemo(() => {
    const map = new Map<string, Policy>();
    for (const p of profilePolicies) map.set(p.id, p);
    return map;
  }, [profilePolicies]);

  // The single policy being nudged — its pending-provider request row(s).
  const policyRequests = useMemo(
    () =>
      profileRequests.filter(
        (r) =>
          r.policyId === policyId &&
          deriveDisplayStatus(r.status, r.actions) === 'PENDING_PROVIDER',
      ),
    [profileRequests, policyId],
  );

  // Only deviations from the server default get tracked here. Undefined
  // keys mean "use the default from the preview response" — mirrors the
  // pattern CoAEmailCraftingStep uses and avoids the TiptapEditor mount
  // race that previously left the body empty when initial state was ''
  // seeded via useEffect.
  const [edits, setEdits] = useState<{ subject?: string; body?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const defaultSubject = nudgeData?.defaultTemplate.subject ?? '';
  const defaultBody = nudgeData?.defaultTemplate.bodyHtml ?? '';
  const subject = edits.subject ?? defaultSubject;
  const body = edits.body ?? defaultBody;

  const handleSubjectChange = useCallback(
    (next: string) =>
      setEdits((prev) => (next === defaultSubject ? { ...prev, subject: undefined } : { ...prev, subject: next })),
    [defaultSubject],
  );
  const handleBodyChange = useCallback(
    (next: string) =>
      setEdits((prev) => (next === defaultBody ? { ...prev, body: undefined } : { ...prev, body: next })),
    [defaultBody],
  );

  const isEmailModified = useMemo(() => {
    return (edits.subject !== undefined && edits.subject !== defaultSubject) ||
           (edits.body !== undefined && edits.body !== defaultBody);
  }, [edits, defaultSubject, defaultBody]);

  const handleReset = useCallback(() => setEdits({}), []);

  const handleCancel = useCallback(() => {
    navigate(relationshipId ? `/relationships/${relationshipId}` : '/');
  }, [navigate, relationshipId]);

  const handleSend = useCallback(async () => {
    if (!nudgeData) return;
    setSubmitError(null);

    try {
      const request = {
        policyId: nudgeData.policyInfo.policyId,
        loaId: nudgeData.loaInfo.loaId,
        recipientEmail: nudgeData.policyInfo.providerEmail,
        subject,
        bodyHtml: body,
        // Always ask the server to attach the policy's LOA slice — the
        // preview response does not carry a per-policy slice-availability
        // flag, and the backend already falls back to "send without
        // attachment" (with a warn log) when no slice exists. Gating the
        // flag on adapter-hardcoded `hasLoaSlice: false` previously caused
        // every nudge to ship without the LOA PDF.
        includeAttachment: true,
        acknowledgeWarning: nudgeData.rateLimitStatus.warningCode !== 'NONE',
      };
      const result = isCoaFlow
        ? await sendCoa.mutateAsync(request)
        : await sendLoa.mutateAsync(request);

      if (result.success) {
        showToast(
          <>
            <span className="font-bold">{isCoaFlow ? 'CoA' : 'Nudge'} email</span>
            {' '}sent to{' '}
            <span className="font-bold">{nudgeData.policyInfo.providerEmail}</span>
          </>,
        );
        handleCancel();
      } else {
        setSubmitError(result.errorMessage || result.message || 'Failed to send nudge email');
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to send nudge email');
    }
  }, [nudgeData, subject, body, isCoaFlow, sendCoa, sendLoa, showToast, handleCancel]);

  // Wait for preview before mounting composer — avoids TiptapEditor
  // mounting with content='' and getting stuck on empty-state (same guard
  // as CoAEmailCraftingStep).
  if (isLoading || !nudgeData) {
    return (
      <div className="w-full py-8 px-6">
        <div className="flex items-center justify-center py-16">
          {fetchError ? (
            <span className="text-sm text-red-600">
              {fetchError.message || 'Failed to load nudge data'}
            </span>
          ) : (
            <>
              <div className="w-6 h-6 border-2 border-purple-primary border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-grey-02">Loading nudge template...</span>
            </>
          )}
        </div>
      </div>
    );
  }

  const allowNudge = canNudge(nudgeData.rateLimitStatus);
  const sending = isCoaFlow ? sendCoa.isPending : sendLoa.isPending;

  return (
    <EmailComposerPanel
      subject={subject}
      onSubjectChange={handleSubjectChange}
      subjectPlaceholder={defaultSubject}
      recipients={nudgeData.policyInfo.providerEmail}
      body={body}
      onBodyChange={handleBodyChange}
      isModified={isEmailModified}
      onReset={handleReset}
      onCancel={handleCancel}
      onSend={handleSend}
      sendLabel={isCoaFlow ? 'Send CoA Nudge' : 'Send Nudge Email'}
      sending={sending}
      sendDisabled={!allowNudge}
      error={submitError}
      footer={
        isCoaFlow ? undefined : (
          <div className="mx-lg mt-lg mb-lg">
            <NudgeHistoryDisplay history={nudgeData.nudgeHistory} />
          </div>
        )
      }
    >
      <RecipientInfoCard
        title={nudgeData.policyInfo.providerName}
        email={nudgeData.policyInfo.providerEmail}
      />

      <PoliciesCard
        title={isCoaFlow ? 'Change of Agency target' : 'Policy requiring follow-up'}
        description={
          isCoaFlow
            ? 'This is the policy the Change of Agency covers; the provider is being nudged to action it.'
            : 'This is the policy the provider is being nudged about.'
        }
      >
        <OutstandingRequestsTable
          requests={policyRequests}
          policyById={policyById}
          testId="outstanding-requests-table"
        />
      </PoliciesCard>

      {!isCoaFlow && (
        <div className="mx-lg mt-md">
          <NudgeRateLimitWarning rateLimitStatus={nudgeData.rateLimitStatus} />
        </div>
      )}
    </EmailComposerPanel>
  );
}

export default NudgeProviderPage;
